import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import React from 'react';

import { getVisibleMyReportsColumns, MY_REPORTS_ACTIONS_COL_WIDTH } from './myReportsColumns';
import { MyReportsSortModel } from './myReportsListHelpers';
import { MyReport } from './MyReportsTable.types';
import { MyReportsTableHead } from './MyReportsTableHead';
import { MyReportsTableRow } from './MyReportsTableRow';
import {
  GroupHeaderRow,
  MyReportsStatusRow,
  MyReportsStatusVariant,
} from './MyReportsTableStatusRows';
import { buildColLayout, stickyActionsTableSx } from '../../../configs/tableStyles';
import { useIsClient } from '../../../hooks/useIsClient';

export interface MyReportsTableProps {
  myReports: MyReport[];
  sharedReports: MyReport[];
  otherReports: MyReport[];
  mySiblingId: number | undefined;
  sortModel: MyReportsSortModel;
  onSortChange: (model: MyReportsSortModel) => void;
  onEdit: (row: MyReport) => void;
  onDelete: (row: MyReport) => void;
  loading?: boolean;
  /** The reports request failed — shown instead of an empty-list message. */
  isError?: boolean;
  /** Distinguishes "your filters matched nothing" from "you own no reports". */
  hasActiveFilters?: boolean;
  /**
   * When set, clicking a report name calls this instead of navigating. Also
   * drops the Page column, which is redundant once the list is pinned to one page.
   */
  onSelectReport?: (row: MyReport) => void;
  /** Whether a report is pinned as a tab on its own list page. */
  isPinned?: (row: MyReport) => boolean;
  /** When set, each row offers a pin toggle. */
  onTogglePin?: (row: MyReport) => void;
  /** When set, each row offers a default (star) toggle. */
  onToggleDefault?: (row: MyReport) => void;
}

const canUserDelete = (row: MyReport, mySiblingId: number | undefined): boolean => {
  if (mySiblingId == null) return false;
  if (row.reportOwner?.id === mySiblingId) return true;
  return !!row.savedReportAccess?.some(
    (access) => access.entityId === mySiblingId && access.isActive !== false
  );
};

const emptyVariant = (
  loading: boolean,
  isError: boolean,
  hasActiveFilters: boolean
): MyReportsStatusVariant => {
  if (loading) return 'loading';
  if (isError) return 'error';
  return hasActiveFilters ? 'no-results' : 'no-reports';
};

export const MyReportsTable: React.FC<MyReportsTableProps> = ({
  myReports,
  sharedReports,
  otherReports,
  mySiblingId,
  sortModel,
  onSortChange,
  onEdit,
  onDelete,
  loading = false,
  isError = false,
  hasActiveFilters = false,
  onSelectReport,
  isPinned,
  onTogglePin,
  onToggleDefault,
}) => {
  const isClient = useIsClient();
  const isEmbedded = onSelectReport != null;
  const columns = React.useMemo(
    () =>
      getVisibleMyReportsColumns(isClient).filter((col) => !(isEmbedded && col.key === 'pageName')),
    [isClient, isEmbedded]
  );
  const { widths, minTableWidth } = React.useMemo(
    () => buildColLayout(columns, MY_REPORTS_ACTIONS_COL_WIDTH),
    [columns]
  );
  const totalCols = columns.length + 1;
  const isEmpty = myReports.length + sharedReports.length + otherReports.length === 0;

  const renderGroup = (label: string, rows: MyReport[], startIndex: number) => {
    if (rows.length === 0) return null;
    return (
      <>
        <GroupHeaderRow label={label} count={rows.length} colSpan={totalCols} />
        {rows.map((row, i) => {
          const isOwner = row.reportOwner?.id === mySiblingId;
          return (
            <MyReportsTableRow
              key={row.id}
              row={row}
              columns={columns}
              isClient={isClient}
              mySiblingId={mySiblingId}
              canEdit={isOwner}
              // Client/customer users may remove reports they own, but must not
              // unshare reports shared with them — only that path is suppressed.
              canDelete={canUserDelete(row, mySiblingId) && (isOwner || !isClient)}
              onEdit={() => onEdit(row)}
              onDelete={() => onDelete(row)}
              onSelect={onSelectReport ? () => onSelectReport(row) : undefined}
              isPinned={isPinned?.(row)}
              onTogglePin={onTogglePin ? () => onTogglePin(row) : undefined}
              onToggleDefault={onToggleDefault ? () => onToggleDefault(row) : undefined}
              even={(startIndex + i) % 2 === 1}
            />
          );
        })}
      </>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* With rows on screen the spinner is a scrim over them. With none it would
          float over a zero-height body, so the empty state renders it in-table. */}
      {loading && !isEmpty && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={32} sx={{ color: 'v4.accent' }} />
        </Box>
      )}
      <TableContainer>
        <Table
          size="small"
          aria-label="My Reports table"
          sx={{
            width: '100%',
            minWidth: minTableWidth,
            tableLayout: 'fixed',
            // No `elasticLastColumnSx`: the colgroup sizes every column, so this
            // table already fills its container. The stretch would beat the
            // percentage `calc()` widths and collapse every flex column to 0px
            // whenever one of them sorts last.
            ...stickyActionsTableSx,
          }}
        >
          <colgroup>
            {columns.map((col, i) => (
              <col key={col.key} style={{ width: widths[i] }} />
            ))}
            <col style={{ width: MY_REPORTS_ACTIONS_COL_WIDTH }} />
          </colgroup>
          <MyReportsTableHead columns={columns} sortModel={sortModel} onSortChange={onSortChange} />
          <TableBody>
            {isEmpty && (
              <MyReportsStatusRow
                variant={emptyVariant(loading, isError, hasActiveFilters)}
                colSpan={totalCols}
              />
            )}
            {renderGroup('My Reports', myReports, 0)}
            {renderGroup('Shared With Me', sharedReports, myReports.length)}
            {renderGroup('Other', otherReports, myReports.length + sharedReports.length)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
